import { Button, CircularProgress, useTheme } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';

const animateScaleX = keyframes`
  0% {
    transform: scaleX(1);
  }
  50% {
    transform: scaleX(1.05);
  }
  100% {
    transform: scaleX(1);
  }
`;

const OutlinedButtonWrapper = styled.div`
	display: inline-block;
`;

const StyledOutlinedButton = styled(Button)`
	&& {
		background: transparent;
		border: 1px solid ${props => props.themeColor};
		color: ${props => props.themeColor} !important;
		outline: none;
		padding: 18px 20px;
		height: 30px;
		border-radius: 10px;
		overflow: hidden;
		transform: scaleX(1);
		transition: transform 0.5s cubic-bezier(0.4, 0, 0, 1);
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		min-width: ${props => props.minWidth || 'auto'};
		width: ${props => (props.fullWidth ? '100%' : 'auto')};
		opacity: ${props => (props.disabled ? 0.6 : 1)};
		pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
		text-transform: none;

		&:hover {
			animation: ${animateScaleX} 0.6s cubic-bezier(0.4, 0, 0, 1);
			background: transparent;
			color: ${props => props.isDarkMode ? '#000' : '#fff'} !important;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			bottom: 0;
			background: ${props => props.isDarkMode ? '#fff' : '#000'};
			transition: transform 0.5s cubic-bezier(0.4, 0, 0, 1), border-radius 0.5s cubic-bezier(0.4, 0, 0, 1);
			width: 100%;
			height: 100%;
			border-radius: 50% 50% 0 0;
			transform: translateY(100%);
			z-index: -1;
		}

		&:hover::after {
			transform: translateY(0%);
			border-radius: 0;
		}

		&:focus {
			outline: none;
		}

		.MuiButton-label {
			position: relative;
			z-index: 99;
			color: ${props => props.themeColor};
			display: flex;
			align-items: center;
			gap: 2px;
			transition: color 0.5s cubic-bezier(0.4, 0, 0, 1);
		}

		&:hover .MuiButton-label {
			color: ${props => props.isDarkMode ? '#000' : '#fff'};
			z-index: 99;
		}

		.MuiButton-startIcon,
		.MuiButton-endIcon {
			position: relative;
			z-index: 1;
			transition: color 0.5s cubic-bezier(0.4, 0, 0, 1);
		}

		&:hover .MuiButton-startIcon,
		&:hover .MuiButton-endIcon {
			color: ${props => props.isDarkMode ? '#000' : '#fff'};
		}
			
		&:hover .MuiButton-startIcon,
		&:hover .MuiButton-endIcon {
			color: ${props => props.isDarkMode ? '#000' : '#fff'};
		}

		.omni-loader {
			color: ${props => props.themeColor} !important;
		}
	}
`;

// Content wrapper to maintain button size during loading
const ButtonContent = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: relative;
`;

const LabelWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	visibility: ${props => props.$isLoading ? 'hidden' : 'visible'};
`;

const LoaderWrapper = styled.span`
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

// Icon style for buttonType icons
const ButtonTypeIcon = styled.span`
	display: inline-flex;
	align-items: center;
	margin-right: 6px;
	
	& .MuiSvgIcon-root {
		font-size: 18px;
	}
`;

// Map buttonType to corresponding icon
const getButtonTypeIcon = (buttonType) => {
	switch (buttonType?.toLowerCase()) {
		case 'export':
			return <FileDownloadIcon />;
		case 'filters':
		case 'filter':
			return <FilterListIcon />;
		case 'create':
		case 'add':
			return <AddIcon />;
		default:
			return null;
	}
};

const ButtonOmniNew = ({
	label,
	onClick,
	variant = 'outlined',
	color = 'primary',
	size = 'medium',
	fullWidth = false,
	startIcon = null,
	endIcon = null,
	loading = false,
	disabled = false,
	style = {},
	className = '',
	disableSpecialStyling = false,
	buttonType = null,
	...otherProps
}) => {
	// Use theme hooks instead of localStorage
	const theme = useTheme();
	const mainTheme = useSelector(({ fuse }) => fuse.settings.mainTheme);

	// Get theme color from theme object with fallback
	const themeColor = theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff';

	const isSelectButton = endIcon && !loading;
	const shouldApplySpecialStyling = variant === 'outlined' && !isSelectButton && !disableSpecialStyling;
	const isDarkMode = mainTheme === 'mainThemeDark' || theme.palette.type === 'dark';

	// Get icon based on buttonType (if no startIcon is provided)
	const buttonTypeIcon = !startIcon ? getButtonTypeIcon(buttonType) : null;

	// Determine loader color based on theme and variant
	// In light mode: use dark color for visibility
	// In dark mode: use light color for visibility
	const getLoaderColor = () => {
		if (variant === 'contained') {
			// For contained buttons, use contrasting color to the button background
			return isDarkMode ? '#000' : '#fff';
		}
		// For outlined/text variants
		// Light mode: black loader on transparent/white background
		// Dark mode: white loader on dark background
		return isDarkMode ? '#fff' : '#000';
	};

	// Render label with buttonType icon
	const renderLabelContent = () => {
		if (buttonTypeIcon) {
			return (
				<>
					<ButtonTypeIcon>{buttonTypeIcon}</ButtonTypeIcon>
					{label}
				</>
			);
		}
		return label;
	};

	// Render button content with loader overlay (maintains button size)
	const renderButtonContent = () => {
		return (
			<ButtonContent>
				<LabelWrapper $isLoading={loading}>
					{renderLabelContent()}
				</LabelWrapper>
				{loading && (
					<LoaderWrapper>
						<CircularProgress
							size={18}
							style={{ color: themeColor }}
						/>
					</LoaderWrapper>
				)}
			</ButtonContent>
		);
	};

	if (shouldApplySpecialStyling) {
		return (
			<OutlinedButtonWrapper style={style} className={className}>
				<StyledOutlinedButton
					onClick={onClick}
					variant={variant}
					color={color}
					size={size}
					fullWidth={fullWidth}
					startIcon={!loading ? startIcon : null}
					endIcon={!loading ? endIcon : null}
					disabled={loading || disabled}
					themeColor={themeColor}
					isDarkMode={isDarkMode}
					{...otherProps}
				>
					{renderButtonContent()}
				</StyledOutlinedButton>
			</OutlinedButtonWrapper>
		);
	}

	return (
		<Button
			onClick={onClick}
			variant={variant}
			color={color}
			size={size}
			fullWidth={fullWidth}
			startIcon={!loading ? startIcon : null}
			endIcon={!loading ? endIcon : null}
			disabled={loading || disabled}
			style={{ textTransform: 'none', ...style }}
			className={`shimmer-button ${className}`}
			{...otherProps}
		>
			{renderButtonContent()}
		</Button>
	);
};

export default ButtonOmniNew;